// useStudioCreate — the Studio's one way to make a part (pen "Studio ·
// Create menu", spec §5; #317): the one Create menu from the outline's
// foot, an empty page, a line's margin and "/" at the caret, all through
// the same commands:
//  - a kind (Module, Page, Quiz, Document) is planned empty (`planPart`)
//    where the domain's placement rule puts it (a document where the author
//    is, else in the project's course, else on the desk itself; a module in
//    the course, else on the desk), under a numbered default name in the
//    author's words;
//  - the author's own words, or a kind made from a source, run the one
//    create command (`createFromSources`, #274) with the place shown;
//    without a source, the words become the brief of a planned page (the
//    assistant writes no content without a source: principle 7).
// Which menu is open, at which step, is view state (`ask`): "/" opens it
// at the caret on the sources (pen Q73 step 1), the buttons at the start;
// back (or Escape) goes one step back, then closes it.
// Ask here for one source (pen Q73 step 2) is the same line, scoped: its
// request names that source only (the create command refuses a source
// that is not the project's).
// Ask here is written in the line itself (pen Q66), never in a field: the
// line that asks is view state too (`asking`): the block at the caret, or
// an empty line on the page where the part would land (from a button).
// Only Enter sends; one request runs once at a time; a request that fails
// to send puts the line back with its words (nothing lost).
// Every command reloads the library so the pages project the result; a
// refusal is a notice (the domain's reason).

export type { CreateAt, StudioCreate };
export { useStudioCreate };

import { exhaustSwitchCase, Result } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type Dispatch, type SetStateAction, useRef, useState } from "react";
import { deskBinders } from "../../components/Studio/studioDesk.js";
import {
    type CreateKind,
    type CreateStep,
    type FromSourceKind,
    plannableOf,
    stepBack,
} from "../../components/StudioEditor/studioCreate.js";
import type { StudioSlashKey } from "../../components/StudioEditor/studioSlash.js";
import { numberedDefaultName } from "../createDefaults.js";
import type { ExternalError } from "../externalError.js";
import type { HostNotice } from "../frame.js";
import type { CreateWhat, PartToPlan } from "../ports/brief.js";
import type { PortResult } from "../ports/result.js";
import type { AppPorts, Library } from "../ports.js";
import type { StudioPlace } from "./useStudioPlace.js";

// Where a Create menu opens: the outline's foot, the first line of an empty
// page, the + in the margin of a line (by the part's id), or at the caret
// in a block (by the block's id) once "/" picked Ask here or Create from a
// source….
type CreateAt = "outline" | "page" | `line:${string}` | `caret:${string}`;

// The Create menu open: where it opened, and at which step.
type CreateAsk = {
    readonly at: CreateAt;
    readonly step: CreateStep;
};

// Where in its container a new part goes: at `index` (0-based; the + under
// a line), or at the end (absent).
type CreateIndex = number | undefined;

// The line that asks, with the words written in it so far:
//  - in_text: the block at the caret where "/" picked Ask here;
//  - in_page: an empty line on the page of `container` (absent: the
//    project's own page) at `index`, from "+ Create" or a line's +.
// `source`: the one source the request is for (pen Q73; absent: the
// project's sources).
type Asking =
    | {
          readonly type: "in_text";
          readonly anchor: string;
          readonly words: string;
          readonly source?: string;
      }
    | {
          readonly type: "in_page";
          readonly container: string | undefined;
          readonly index: CreateIndex;
          readonly words: string;
          readonly source?: string;
      };

type StudioCreate = {
    readonly ask: CreateAsk | null;
    // The block a step opened from "/" sits at (absent: none open).
    readonly caret: string | undefined;
    readonly open: (ask: CreateAsk) => void;
    readonly close: () => void;
    // Back one step from the step shown (the head's chevron, Escape); from
    // the first step the menu closes.
    readonly back: (shown: CreateStep) => void;
    // A pick from "/" at the caret in the block `anchor`, with the words
    // typed after "/" and whether Enter picked it (only Enter sends); a part
    // made there goes to `index` in the page's container (a course or module
    // page: after the parts above the caret; absent: its end).
    readonly slash: (
        key: StudioSlashKey,
        anchor: string,
        words: string,
        enter: boolean,
        index: CreateIndex,
    ) => void;
    readonly asking: Asking | null;
    // Ask here from a button: a line where the part would land, starting
    // with `words` (what was typed in the menu's filter; often nothing).
    readonly askHere: (
        words: string,
        container: string | undefined,
        index: CreateIndex,
        source?: string,
    ) => void;
    // Ask here for one source at the caret: the block `anchor` becomes the
    // line that asks, scoped to `source`.
    readonly askInLine: (anchor: string, source: string) => void;
    // Enter in the line: send what it says.
    readonly askSend: (words: string) => void;
    // Esc or a click elsewhere: the line goes (its words stay as text where
    // there is text).
    readonly askLeave: () => void;
    // Plan an empty part of `kind` at the end of `container` (absent: where
    // the author is).
    readonly insert: (
        kind: CreateKind,
        container: string | undefined,
        index?: CreateIndex,
    ) => void;
    // Make a kind from one source.
    readonly fromSource: (
        source: string,
        kind: FromSourceKind,
        container: string | undefined,
        index?: CreateIndex,
    ) => void;
};

type StudioCreateDeps = {
    readonly ports: AppPorts;
    readonly library: Library | null;
    readonly setLibrary: Dispatch<SetStateAction<Library | null>>;
    readonly showNotice: (notice: HostNotice) => void;
    readonly displayText: (error: ExternalError) => string;
    readonly place: StudioPlace;
    readonly project: string | null;
};

// The longest title a plan takes from the author's words.
const TITLE_FROM_WORDS_MAX = 60;

const titleFromWords = (words: string): string => {
    const line = words.trim().split("\n")[0] ?? "";
    return line.length <= TITLE_FROM_WORDS_MAX
        ? line
        : `${line.slice(0, TITLE_FROM_WORDS_MAX - 1).trimEnd()}…`;
};

const useStudioCreate = ({
    ports,
    library,
    setLibrary,
    showNotice,
    displayText,
    place,
    project,
}: StudioCreateDeps): StudioCreate => {
    const { t } = useLingui();
    const [ask, setAsk] = useState<CreateAsk | null>(null);
    const [asking, setAsking] = useState<Asking | null>(null);
    // The requests on their way (by place and words): the same one never
    // starts twice (a repeated Enter, SF-13). Not view state: no render reads
    // it.
    const sending = useRef(new Set<string>());

    const projectItem = (library?.projects ?? []).find((p) => p.id === project);
    const onCanvas = new Set((projectItem?.canvas ?? []).map((i) => i.target));
    const materials = library?.materials ?? [];
    const titleOf = (id: string): string =>
        materials.find((m) => m.id === id)?.title ?? "";
    // The project's sources (on its canvas), in the library's order.
    const sources = (library?.sources ?? [])
        .filter((s) => onCanvas.has(s.id))
        .map((s) => s.id);

    // The container shown (or the one the document shown lies in): where
    // the author is, for the domain's placement rule.
    const focusOf = (container: string | undefined): string | undefined =>
        container ?? deskBinders(place.desk).at(-1)?.id;
    // The project's one course, if it has exactly one.
    const course = (): string | undefined => {
        const [only, ...more] = materials.filter(
            (m) => onCanvas.has(m.id) && m.container?.kind === "course",
        );
        return only !== undefined && more.length === 0 ? only.id : undefined;
    };
    // Where the author is: the focus, else the project's one course.
    const landing = (container: string | undefined): string | undefined =>
        focusOf(container) ?? course();
    const siblings = (container: string): readonly string[] =>
        (materials.find((m) => m.id === container)?.parts ?? []).map(titleOf);
    // The names a new part's default name must not take: those beside it
    // (a module lies in the course, a document where the author is), else
    // those on the desk.
    const takenNear = (at: string | undefined): readonly string[] =>
        at === undefined
            ? (projectItem?.canvas ?? []).map((i) => titleOf(i.target))
            : siblings(at);

    const wordOf = (kind: CreateKind): string => {
        switch (kind) {
            case "module":
                return t`New module`;
            case "page":
                return t`New page`;
            case "quiz":
                return t`New quiz`;
            case "document":
                return t`New document`;
            default:
                return exhaustSwitchCase(kind);
        }
    };

    // `settled`: told whether it went through (absent: nobody asks).
    const run = (
        command: () => PortResult<unknown>,
        settled?: (ok: boolean) => void,
    ): void => {
        setAsk(null);
        const go = async () => {
            const r = await command();
            if (Result.isErr(r)) {
                showNotice({
                    type: "lifecycle-failed",
                    message: displayText(r.error),
                });
                settled?.(false);
                return;
            }
            settled?.(true);
            const lib = await ports.loadLibrary();
            if (Result.isOk(lib)) setLibrary(lib.value);
        };
        void go();
    };

    // Outside a project there is no desk to plan on: say so.
    const nowhere = (settled?: (ok: boolean) => void) => {
        setAsk(null);
        showNotice({
            type: "lifecycle-failed",
            message: t`Open a project first, then add to it.`,
        });
        settled?.(false);
    };

    // Plan one empty part where the domain puts it (`planPart`), then move
    // it to `index` in `container` when it landed there (absent: it stays
    // at the end).
    const plan = (
        part: PartToPlan,
        title: string,
        container: string | undefined,
        index: CreateIndex,
        settled?: (ok: boolean) => void,
    ) => {
        if (project === null) return nowhere(settled);
        const focus = focusOf(container);
        run(async () => {
            const made = await ports.planPart({
                project,
                part,
                title,
                ...(focus === undefined ? {} : { focus }),
            });
            return Result.isErr(made) ||
                index === undefined ||
                container === undefined ||
                made.value.container !== container
                ? made
                : ports.reorderPart(container, made.value.part, index);
        }, settled);
    };

    // Make a part in `container`, then move it to `index` there (absent:
    // it stays at the end). `command` answers the new part's id.
    const placed = (
        container: string,
        index: CreateIndex,
        command: () => PortResult<string>,
        settled?: (ok: boolean) => void,
    ) =>
        run(async () => {
            const made = await command();
            return index === undefined || Result.isErr(made)
                ? made
                : ports.reorderPart(container, made.value, index);
        }, settled);

    const insert = (
        kind: CreateKind,
        container: string | undefined,
        index?: CreateIndex,
    ) => {
        const name = numberedDefaultName(
            takenNear(kind === "module" ? course() : landing(container)),
            wordOf(kind),
        );
        return kind === "module"
            ? plan({ type: "module" }, name, container, index)
            : plan(
                  { type: "document", kind: plannableOf(kind) },
                  name,
                  container,
                  index,
              );
    };

    const create = (
        from: readonly [string, ...string[]],
        what: CreateWhat,
        title: (at: string | undefined) => string,
        container: string | undefined,
        index: CreateIndex,
        settled?: (ok: boolean) => void,
    ) => {
        const at = landing(container);
        const made = () =>
            ports.createFromSources({
                from,
                what,
                title: title(at),
                ...(at === undefined
                    ? {
                          where: {
                              type: "new_module",
                              ...(project === null ? {} : { project }),
                          },
                      }
                    : { where: { type: "end_of_container", container: at } }),
                ...(project === null ? {} : { project }),
            });
        if (at === undefined) return run(made, settled);
        placed(
            at,
            index,
            async () =>
                Result.map((c: { readonly material: string }) => c.material)(
                    await made(),
                ),
            settled,
        );
    };

    // Make from the author's words (an untrusted request: it goes to the
    // one create command as written, which parses it at the door): from
    // the project's sources when it has any, else a planned page with them
    // as its brief; for one source only when `source` names it (its text is
    // data the request reads, never an instruction). `failed`: refused (the
    // words go back to their line).
    const say = (
        words: string,
        container: string | undefined,
        index: CreateIndex,
        failed: () => void,
        source: string | undefined,
    ) => {
        const text = words.trim();
        const key = `${container ?? ""}\n${source ?? ""}\n${text}`;
        if (text === "" || sending.current.has(key)) return;
        sending.current.add(key);
        const settled = (ok: boolean) => {
            sending.current.delete(key);
            if (!ok) failed();
        };
        const [first, ...rest] = source === undefined ? sources : [source];
        if (first !== undefined)
            return create(
                [first, ...rest],
                { type: "instruction", instruction: text },
                () => titleFromWords(text),
                container,
                index,
                settled,
            );
        plan(
            { type: "document", kind: "page", brief: text },
            titleFromWords(text),
            container,
            index,
            settled,
        );
    };

    // Send what the line says; refused, the line comes back with its words.
    const send = (line: Asking, words: string) => {
        setAsking(null);
        const back = () => setAsking({ ...line, words });
        switch (line.type) {
            case "in_text":
                return say(words, undefined, undefined, back, line.source);
            case "in_page":
                return say(
                    words,
                    line.container,
                    line.index,
                    back,
                    line.source,
                );
            default:
                return exhaustSwitchCase(line);
        }
    };

    const slash = (
        key: StudioSlashKey,
        anchor: string,
        words: string,
        enter: boolean,
        index: CreateIndex,
    ) => {
        switch (key.type) {
            case "ask_here": {
                const line: Asking = { type: "in_text", anchor, words };
                // Enter with words sends them (scene A); else the line
                // itself takes the request (scene B).
                return enter && words.trim() !== ""
                    ? send(line, words)
                    : setAsking(line);
            }
            case "from_source":
                return setAsk({
                    at: `caret:${anchor}`,
                    step: { type: "sources" },
                });
            case "insert":
                return insert(key.kind, undefined, index);
            default:
                return exhaustSwitchCase(key);
        }
    };

    return {
        ask,
        caret: ask?.at.startsWith("caret:")
            ? ask.at.slice("caret:".length)
            : undefined,
        open: setAsk,
        close: () => setAsk(null),
        back: (shown) => {
            if (ask === null) return;
            const to = stepBack(
                shown,
                ask.at.startsWith("caret:") ? "caret" : "button",
            );
            setAsk(to === null ? null : { at: ask.at, step: to });
        },
        slash,
        asking,
        askHere: (words, container, index, source) => {
            setAsk(null);
            setAsking({
                type: "in_page",
                container,
                index,
                words,
                ...(source === undefined ? {} : { source }),
            });
        },
        askInLine: (anchor, source) => {
            setAsk(null);
            setAsking({ type: "in_text", anchor, words: "", source });
        },
        askSend: (words) => {
            if (asking !== null) send(asking, words);
        },
        askLeave: () => setAsking(null),
        insert,
        fromSource: (source, kind, container, index) =>
            create(
                [source],
                { type: "material_kind", kind: plannableOf(kind) },
                (at) =>
                    numberedDefaultName(
                        at === undefined ? [] : siblings(at),
                        wordOf(kind),
                    ),
                container,
                index,
            ),
    };
};
